<script setup>
import { computed, ref, onMounted, nextTick, getCurrentInstance } from 'vue';
import Icon from '../icon/Icon.vue';

const props = defineProps({
  modelValue: { type: [String, Number], default: '' },
  /**
   * Native type. Adds two non-native pseudo-types that switch the rendered
   * element: 'textarea' (renders <textarea>) and 'select' (renders <select>).
   */
  type: { type: String, default: 'text' },
  customInputClass: { type: [String, Object, Array], default: '' },
  placeholder: { type: String, default: '' },
  label: { type: String, default: '' },
  id: { type: String, default: '' },
  size: {
    type: String,
    default: 'md',
    validator: value => ['sm', 'md'].includes(value),
  },
  /** Legacy combined message — kept for backward compatibility. */
  message: { type: String, default: '' },
  /** New API: explicit error message (takes precedence over message). */
  error: { type: String, default: '' },
  /** New API: explicit helper text (used when error is empty). */
  hint: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  required: { type: Boolean, default: false },
  messageType: {
    type: String,
    default: 'info',
    validator: value => ['info', 'error', 'success'].includes(value),
  },
  min: { type: String, default: '' },
  max: { type: String, default: '' },
  autofocus: { type: Boolean, default: false },
  /** Textarea rows when type='textarea'. */
  rows: { type: Number, default: 4 },
  /** Select options when type='select'. {value, label} or plain strings. */
  options: { type: Array, default: () => [] },
  /** Leading icon (lucide / ph / ri). Auto-applied for type='search'. */
  leadingIcon: { type: [String, Object, Function], default: '' },
  /** Trailing icon. Ignored when type='password' (we render a visibility toggle). */
  trailingIcon: { type: [String, Object, Function], default: '' },
});

const emit = defineEmits([
  'update:modelValue',
  'blur',
  'input',
  'focus',
  'enter',
]);

// Generate a unique ID per component instance when `id` prop is not provided.
const { uid } = getCurrentInstance();
const uniqueId = computed(() => props.id || `input-${uid}`);

const isFocused = ref(false);
const inputRef = ref(null);
const passwordVisible = ref(false);

// Resolve message — `error` prop wins over legacy `message`+`messageType`.
const resolvedMessage = computed(() => props.error || props.hint || props.message);
const resolvedMessageType = computed(() => {
  if (props.error) return 'error';
  if (props.hint) return 'info';
  return props.messageType;
});

const messageClass = computed(() => {
  switch (resolvedMessageType.value) {
    case 'error':
      return 'text-n-ruby-9 dark:text-n-ruby-9';
    case 'success':
      return 'text-n-teal-10 dark:text-n-teal-10';
    default:
      return 'text-n-slate-11 dark:text-n-slate-11';
  }
});

const inputOutlineClass = computed(() => {
  switch (resolvedMessageType.value) {
    case 'error':
      return 'outline-n-ruby-8 dark:outline-n-ruby-8 hover:outline-n-ruby-9 dark:hover:outline-n-ruby-9 disabled:outline-n-ruby-8 dark:disabled:outline-n-ruby-8';
    default:
      return 'outline-n-weak dark:outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 disabled:outline-n-weak dark:disabled:outline-n-weak focus:outline-n-brand dark:focus:outline-n-brand';
  }
});

const effectiveLeadingIcon = computed(() => {
  if (props.leadingIcon) return props.leadingIcon;
  if (props.type === 'search') return 'i-lucide-search';
  return '';
});

const showTrailingButton = computed(() => props.type === 'password');
const showTrailingIcon = computed(() => !!props.trailingIcon && props.type !== 'password');

const isTextarea = computed(() => props.type === 'textarea');
const isSelect = computed(() => props.type === 'select');

const nativeType = computed(() => {
  if (props.type === 'password') return passwordVisible.value ? 'text' : 'password';
  if (isTextarea.value || isSelect.value) return undefined;
  return props.type;
});

const normalisedOptions = computed(() =>
  props.options.map(o => (typeof o === 'string' ? { value: o, label: o } : o))
);

const handleInput = event => {
  let value = event.target.value;
  // Convert to number if type is number and value is not empty
  if (props.type === 'number' && value !== '') {
    value = Number(value);
  }
  emit('update:modelValue', value);
  emit('input', event);
};

const handleFocus = event => {
  emit('focus', event);
  isFocused.value = true;
};

const sizeClass = computed(() => {
  if (isTextarea.value) return '!px-3 !py-2';
  switch (props.size) {
    case 'sm':
      return 'h-8 !px-3 !py-2';
    case 'md':
      return 'h-10 !px-3 !py-2.5';
    default:
      return 'h-10 !px-3 !py-2.5';
  }
});

const paddingAdjust = computed(() => {
  // Adjust horizontal padding when icons are present.
  const cls = [];
  if (effectiveLeadingIcon.value) cls.push('!pl-9');
  if (showTrailingButton.value || showTrailingIcon.value || isSelect.value) cls.push('!pr-9');
  return cls.join(' ');
});

const handleBlur = event => {
  emit('blur', event);
  isFocused.value = false;
};

const handleEnter = event => {
  emit('enter', event);
};

const togglePassword = () => {
  passwordVisible.value = !passwordVisible.value;
};

onMounted(() => {
  if (props.autofocus) {
    nextTick(() => {
      inputRef.value?.focus();
    });
  }
});

const sharedFieldClass =
  'block w-full reset-base text-sm !mb-0 outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-200 ease-in-out';

const selectChevronStyle = {
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23748094' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
  backgroundSize: '14px 14px',
};
</script>

<template>
  <div class="relative flex flex-col min-w-0 gap-1">
    <label
      v-if="label"
      :for="uniqueId"
      class="mb-0.5 text-sm font-medium text-n-slate-12"
    >
      {{ label }}<span v-if="required" class="text-n-ruby-9 ml-0.5">*</span>
    </label>
    <!-- Prefix slot retained for backward compatibility -->
    <slot name="prefix" />

    <div class="relative">
      <!-- Leading icon (visible inside the field's left gutter) -->
      <span
        v-if="effectiveLeadingIcon"
        class="absolute inset-y-0 left-3 flex items-center pointer-events-none text-n-slate-10"
      >
        <Icon :icon="effectiveLeadingIcon" class="size-4" />
      </span>

      <!-- Textarea -->
      <textarea
        v-if="isTextarea"
        :id="uniqueId"
        ref="inputRef"
        v-bind="$attrs"
        :value="modelValue"
        :rows="rows"
        :placeholder="placeholder"
        :disabled="disabled"
        :required="required"
        :class="[
          customInputClass,
          inputOutlineClass,
          sizeClass,
          paddingAdjust,
          sharedFieldClass,
          'leading-relaxed resize-y min-h-[6rem]',
          { error: resolvedMessageType === 'error', focus: isFocused },
        ]"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
      />

      <!-- Select -->
      <select
        v-else-if="isSelect"
        :id="uniqueId"
        ref="inputRef"
        v-bind="$attrs"
        :value="modelValue"
        :disabled="disabled"
        :required="required"
        :style="selectChevronStyle"
        :class="[
          customInputClass,
          inputOutlineClass,
          sizeClass,
          paddingAdjust,
          sharedFieldClass,
          'appearance-none',
          { error: resolvedMessageType === 'error', focus: isFocused },
        ]"
        @change="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
      >
        <option v-if="placeholder" value="" disabled>{{ placeholder }}</option>
        <option v-for="opt in normalisedOptions" :key="opt.value" :value="opt.value">
          {{ opt.label }}
        </option>
      </select>

      <!-- Default native input (text/email/password/search/number/tel/url/date/etc.) -->
      <input
        v-else
        :id="uniqueId"
        v-bind="$attrs"
        ref="inputRef"
        :value="modelValue"
        :type="nativeType"
        :placeholder="placeholder"
        :disabled="disabled"
        :required="required"
        :min="['date', 'datetime-local', 'time'].includes(type) ? min : undefined"
        :max="
          ['date', 'datetime-local', 'time', 'number'].includes(type)
            ? max
            : undefined
        "
        :class="[
          customInputClass,
          inputOutlineClass,
          sizeClass,
          paddingAdjust,
          sharedFieldClass,
          '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
          { error: resolvedMessageType === 'error', focus: isFocused },
        ]"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @keyup.enter="handleEnter"
      />

      <!-- Trailing icon (display-only) -->
      <span
        v-if="showTrailingIcon"
        class="absolute inset-y-0 right-3 flex items-center text-n-slate-10 pointer-events-none"
      >
        <Icon :icon="trailingIcon" class="size-4" />
      </span>

      <!-- Password visibility toggle -->
      <button
        v-if="showTrailingButton"
        type="button"
        tabindex="-1"
        :aria-label="passwordVisible ? 'Hide password' : 'Show password'"
        class="absolute inset-y-0 right-2 my-auto h-7 w-7 grid place-items-center text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 rounded-md transition-colors"
        @click="togglePassword"
      >
        <span :class="passwordVisible ? 'i-lucide-eye-off size-4' : 'i-lucide-eye size-4'" />
      </button>
    </div>

    <p
      v-if="resolvedMessage"
      class="min-w-0 mt-1 mb-0 text-xs truncate transition-all duration-200 ease-in-out flex items-center gap-1"
      :class="messageClass"
    >
      <span
        v-if="resolvedMessageType === 'error'"
        class="i-lucide-circle-alert size-3.5 flex-shrink-0"
      />
      {{ resolvedMessage }}
    </p>
  </div>
</template>
